// ============================================================================
// FILE: src/modules/uploads/components/wizard/UploadStep.js
// MODULE: Generic Upload Framework
//
// PURPOSE:
// Upload step for the Generic Upload Framework.
//
// RESPONSIBILITIES:
// - Allow the user to select the prepared Excel file.
// - Display the selected file.
// - Display upload instructions.
// - Notify UploadDrawer when a file is selected.
// - Notify UploadDrawer when the user requests upload.
//
// NOTES:
// - UI component only.
// - Does not call APIs.
// - UploadDrawer remains responsible for upload processing.
// ============================================================================
//
// AUTHOR:
// DizyPro ERP Development Team
// ============================================================================

import React from "react";

// ============================================================================
// Component
// ============================================================================

const UploadStep = ({
    wizard,
    onFileChange,
    onUpload
}) => {

    const selectedFile =
        wizard?.selectedFile || null;

    const fileName =
        selectedFile?.name || "";

    const fileSize =
        selectedFile?.size || 0;

    const formattedFileSize =
        fileSize > 0
            ? `${(fileSize / 1024).toFixed(1)} KB`
            : "";

    const handleFileInputChange = (
        event
    ) => {

        const file =
            event.target.files &&
            event.target.files.length > 0
                ? event.target.files[0]
                : null;

        onFileChange(file);

    };

    const handleUploadClick = () => {

        if (!selectedFile) {

            return;

        }

        onUpload();

    };

    return (

        <div className="upload-step">

            {/* ================================================================
               Step Header
               ================================================================ */}

            <div className="upload-step-header">

                <h4>
                    Upload File
                </h4>

                <p>
                    Select the completed Excel template to begin the
                    upload and validation process.
                </p>

            </div>

            {/* ================================================================
               Upload Card
               ================================================================ */}

            <div className="upload-card">

                <div className="upload-card-header">

                    <h4>
                        Select Excel File
                    </h4>

                </div>

                <div className="upload-card-body">

                    {/* ========================================================
                       Upload Configuration
                       ======================================================== */}

                    <div className="upload-configure-step">

                        <div className="form-group">

                            <label>
                                Module
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                value={
                                    wizard?.moduleName ||
                                    wizard?.moduleCode ||
                                    ""
                                }
                                readOnly
                            />

                        </div>

                        <div className="form-group">

                            <label>
                                Entity
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                value={
                                    wizard?.entityName ||
                                    ""
                                }
                                readOnly
                            />

                        </div>

                        <div className="form-group">

                            <label>
                                Upload Type
                            </label>

                            <input
                                type="text"
                                className="form-control"
                                value={
                                    wizard?.selectedUploadType ||
                                    ""
                                }
                                readOnly
                            />

                        </div>

                        {/* ====================================================
                           File Selection
                           ==================================================== */}

                        <div className="form-group">

                            <label>
                                Excel File
                            </label>

                            <input
                                type="file"
                                className="form-control"
                                accept=".xlsx,.xls"
                                onChange={
                                    handleFileInputChange
                                }
                            />

                        </div>

                    </div>

                    {/* ========================================================
                       Selected File
                       ======================================================== */}

                    {selectedFile && (

                        <div
                            className="upload-success"
                            style={{
                                marginTop: "18px"
                            }}
                        >

                            <strong>
                                Selected File:
                            </strong>

                            <span
                                style={{
                                    marginLeft: "8px"
                                }}
                            >
                                {fileName}
                            </span>

                            {formattedFileSize && (

                                <span
                                    style={{
                                        marginLeft: "8px"
                                    }}
                                >
                                    ({formattedFileSize})
                                </span>

                            )}

                        </div>

                    )}

                    {/* ========================================================
                       Upload Action
                       ======================================================== */}

                    <div
                        style={{
                            marginTop: "20px",
                            paddingTop: "18px",
                            borderTop: "1px solid #e5e7eb"
                        }}
                    >

                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={
                                handleUploadClick
                            }
                            disabled={
                                !selectedFile ||
                                wizard?.loading
                            }
                        >

                            {wizard?.loading
                                ? "Uploading..."
                                : "Upload & Validate"}

                        </button>

                    </div>

                    {/* ========================================================
                       Error
                       ======================================================== */}

                    {wizard?.error && (

                        <div className="upload-error">

                            {wizard.error}

                        </div>

                    )}

                </div>

            </div>

            {/* ================================================================
               Instructions
               ================================================================ */}

            <div className="upload-instructions">

                <h5>
                    Upload Instructions
                </h5>

                <ul>

                    <li>
                        Upload the Excel file prepared using the
                        downloaded template.
                    </li>

                    <li>
                        Only Excel files with .xlsx or .xls extensions
                        are accepted.
                    </li>

                    <li>
                        Do not modify the template column headers.
                    </li>

                    <li>
                        Ensure all required data has been entered before
                        uploading the file.
                    </li>

                    <li>
                        The file will be processed and validated after
                        upload.
                    </li>

                </ul>

            </div>

        </div>

    );

};

export default UploadStep;